import { isAbsence, isWork, PlanBlock, PlanCell, PlanColumn, PlanRow, FREE_CELL, sameWork } from "./plan-types";

/**
 * Очередь строки — подпоследовательность колонок, которыми можно двигать.
 * Из неё исключены прошедшие дни и дни отсутствия: они закреплены за своей датой,
 * а очередь работ их обтекает (ровно как обтекает не показанные выходные).
 */
export interface RowQueue {
    /** Индексы колонок, входящие в очередь, по возрастанию */
    columns: number[];
    /** Значения ячеек в порядке очереди */
    cells: PlanCell[];
}

export function isPinned(cell: PlanCell, column: PlanColumn): boolean {
    return column.isPast || isAbsence(cell);
}

export function readQueue(row: PlanRow, columns: PlanColumn[]): RowQueue {
    const queueColumns: number[] = [];
    const cells: PlanCell[] = [];

    for (let i = 0; i < columns.length; i++) {
        const cell = row.cells[i];
        if (cell && isPinned(cell, columns[i])) {
            continue;
        }

        queueColumns.push(i);
        cells.push(cell || FREE_CELL);
    }

    return { columns: queueColumns, cells };
}

export function writeQueue(row: PlanRow, queue: RowQueue, cells: PlanCell[]): PlanRow {
    const next = row.cells.slice();

    queue.columns.forEach((column, index) => {
        next[column] = cells[index];
    });

    return { ...row, cells: next };
}

/** Индекс колонки -> индекс в очереди. -1, если колонка закреплена. */
export function toQueueIndex(queue: RowQueue, column: number): number {
    return queue.columns.indexOf(column);
}

/** Ближайшая позиция вставки в очередь для колонки (даже если колонка закреплена) */
export function toQueueInsertIndex(queue: RowQueue, column: number): number {
    for (let i = 0; i < queue.columns.length; i++) {
        if (queue.columns[i] >= column) {
            return i;
        }
    }

    return queue.columns.length;
}

/**
 * Непрерывные отрезки в координатах колонок: работы одного проекта и дни одного
 * отсутствия подряд. Отпуск рисуется и двигается такой же полосой, как проект,
 * поэтому и собирается в блок так же.
 *
 * Прошедшие дни тоже попадают в блоки — они видны, просто не редактируются.
 * Отрезок рвётся на смене проекта, раздела или типа отсутствия, на границе работы и
 * отсутствия и на границе текущей даты: благодаря последнему будущая часть работ
 * выделяется независимо от прошлой.
 */
export function blocksOf(row: PlanRow, columns: PlanColumn[]): PlanBlock[] {
    const blocks: PlanBlock[] = [];
    let current: PlanBlock | null = null;

    for (let index = 0; index < columns.length; index++) {
        const cell = row.cells[index];

        if (!isWork(cell) && !isAbsence(cell)) {
            current = null;
            continue;
        }

        const isPast = columns[index].isPast;
        // соседние работы одного проекта — разные блоки: граница работ видна, и «разделить»
        // или «объединить» их можно явно. Новый день без работы прирастает к соседу, как на сервере
        const sameContent = isWork(cell)
            ? !!current &&
              !current.absence &&
              sameWork(current, cell) &&
              (!current.assignmentId || !cell.assignmentId || current.assignmentId === cell.assignmentId)
            : !!current && !!current.absence && current.absence.type === cell.type;

        const continues =
            current && sameContent && current.isPast === isPast && current.start + current.length === index;

        if (continues && current) {
            current.length += 1;

            if (isWork(cell) && !current.assignmentId && cell.assignmentId) {
                current.assignmentId = cell.assignmentId;
            }
        } else {
            current = isWork(cell)
                ? {
                      orderId: cell.orderId,
                      sectionId: cell.sectionId,
                      assignmentId: cell.assignmentId,
                      start: index,
                      length: 1,
                      isPast,
                  }
                : {
                      orderId: "",
                      absence: { type: cell.type, shortName: cell.shortName },
                      start: index,
                      length: 1,
                      isPast,
                  };
            blocks.push(current);
        }
    }

    return blocks;
}

/** Блок, которому принадлежит колонка */
export function blockAt(blocks: PlanBlock[], column: number): PlanBlock | undefined {
    return blocks.find((block) => column >= block.start && column < block.start + block.length);
}
